import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import SpeakerCard from '../components/SpeakerCard';
import RecordingCard from '../components/RecordingCard';
import Footer from '../components/Footer';
import { getRecordings, getSpeakers } from '../services/api';
import { Play, Sparkles, ChevronRight, Shuffle, Quote, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import DailyShiurSignupCard from '../components/DailyShiurSignupCard';
import UpcomingHero from '../components/UpcomingHero';
import ContinueListening from '../components/ContinueListening';
import HomeFeaturedSeries from '../components/HomeFeaturedSeries';
import { primePaletteOrder } from '../utils/speakerPalette';
import { SHOW_EMAIL_SUB } from '../config/flags';
import { useSearch } from '../contexts/SearchContext';
import AccessPill from '../components/access/AccessPill';

const FeaturedSection = ({ recording, navigate, speakers }) => {
  if (!recording || !recording.drive_file_id) return null;

  // Rule 16 / Rule 20: never use a translucent cutout on a coloured
  // backdrop. Look the speaker up in the home grid and reuse the same
  // real / curated cover image we render on their SpeakerCard. Falls
  // back to a gold Play icon only when the speaker has NO photo yet.
  const speakerMeta = (speakers || []).find(
    (s) => s && s.name && s.name === recording.speaker,
  );
  const speakerImg = speakerMeta && typeof speakerMeta.image === 'string'
    && (speakerMeta.image.startsWith('http') || speakerMeta.image.startsWith('/'))
    ? speakerMeta.image
    : null;

  return (
    <section className="mb-12" data-testid="home-daily-featured">
      <div className="flex items-center gap-2 mb-6">
        <Sparkles className="w-5 h-5 text-[#9a7b24]" />
        <h2 className="text-[#0f1729] text-xl font-semibold">Daily Featured</h2>
      </div>

      <div className="bg-white rounded-xl overflow-hidden flex flex-col md:flex-row shadow-sm">
        <div className="md:w-2/5 h-64 md:h-auto relative bg-[#0f1729] flex items-center justify-center overflow-hidden">
          {speakerImg ? (
            <img
              src={speakerImg}
              alt={recording.speaker || 'Featured speaker'}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              data-testid="daily-featured-speaker-img"
            />
          ) : (
            <Play className="w-32 h-32 text-[#9a7b24]" />
          )}
          <Badge className="absolute top-4 right-4 bg-[#9a7b24] text-white hover:bg-[#7d6119]">
            ★ Featured
          </Badge>
        </div>

        <div className="md:w-3/5 p-8 flex flex-col justify-center">
          <h3 className="text-4xl font-bold text-[#0f1729] mb-4">
            {recording.title}
          </h3>
          <p className="text-[#8a8f99] mb-2">Speaker: {recording.speaker}</p>
          <p className="text-[#8a8f99] mb-6">Listen to this amazing recording</p>
          <Button
            className="bg-[#9a7b24] text-white hover:bg-[#7d6119] w-fit"
            size="lg"
            onClick={() => navigate(`/player/${recording.drive_file_id}`)}
          >
            <Play className="w-5 h-5 mr-2" />
            Listen Now
          </Button>
        </div>
      </div>
    </section>
  );
};

const RecordingsGrid = ({ title, recordings, navigate }) => {
  if (recordings.length === 0) return null;
  
  return (
    <section className="mb-12">
      <div className="bg-white rounded-lg p-6">
        <h2 className="text-[#0f1729] text-2xl font-semibold mb-4">{title}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-7 gap-3">
          {recordings.map(recording => (
            <RecordingCard key={recording.drive_file_id} recording={recording} queue={recordings} />
          ))}
        </div>
      </div>
    </section>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const { openSearch } = useSearch();
  // Seed from a previous session so the very first render on a return
  // visit shows tiles instantly, before the network round-trip. The
  // Service Worker then delivers a fresh copy in the background and we
  // re-render with the latest data.
  const seedFromLS = (key) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const p = JSON.parse(raw);
      return Array.isArray(p) ? p : null;
    } catch { return null; }
  };
  const seedRecordings = seedFromLS('vaad_home_recordings_v1') || [];
  const seedSpeakers = seedFromLS('vaad_home_speakers_v1') || [];
  const [recordings, setRecordings] = useState(seedRecordings);
  const [speakers, setSpeakers] = useState(seedSpeakers);
  const [topics, setTopics] = useState([]);
  // Daily Featured pick — a recording tied to the current parsha or the
  // next holiday (fetched from /api/calendar/upcoming). Kept in state so
  // the section can still render on the initial synchronous pass with a
  // sensible fallback before this promise resolves.
  const [upcomingFeatured, setUpcomingFeatured] = useState(null);
  // Only show the full-screen spinner if we truly have no data yet.
  const [loading, setLoading] = useState(seedRecordings.length === 0 && seedSpeakers.length === 0);

  const loadData = useCallback(async () => {
    // Feb-2026 speed pass v5: previously we `await Promise.all([reco, spk])`
    // and only hid the "Loading recordings..." spinner once BOTH resolved.
    // But the initial paint (speaker grid) only needs speakers — recordings
    // populate the "Featured / Continue Listening" rows further down. So we
    // now hide the spinner the moment speakers arrive (usually <100 ms
    // warm), let the speaker grid render instantly, and let recordings
    // finish streaming in behind the fold. Both fetches still run in
    // parallel — this only changes when the spinner clears.
    try {
      setLoading(true);
      const speakersPromise = getSpeakers().catch(err => {
        console.error('Error loading speakers:', err);
        return [];
      });
      const recordingsPromise = getRecordings({ limit: 30 }).catch(err => {
        console.error('Error loading recordings:', err);
        return [];
      });

      // Fire recordings, but don't await it before clearing the spinner.
      recordingsPromise.then((recordingsData) => {
        setRecordings(recordingsData || []);
      });

      const speakersData = await speakersPromise;
      // Fall back to whatever recordings arrived so callers below don't
      // see undefined on their first render pass.
      const recordingsData = (await Promise.race([recordingsPromise, Promise.resolve(null)])) || [];
      setRecordings(recordingsData);
      // Homepage speaker order — HaRav Henach Leibowitz is ALWAYS first,
      // then Dovid Harris, Akiva Grunblatt, Shaul Opoczynski. Everyone else
      // sorts by recording count.
      // Suffix-tolerant matching (with or without Zt'l/Ztzl/z"l).
      const normalizeSpeakerName = (n) =>
        (n || '')
          .toLowerCase()
          .replace(/[\u2018\u2019]/g, "'")
          // Strip trailing honorifics: allow apostrophe between any two
          // letters so both "ZT'L" and "Z'TL" get cleaned off (they show
          // up in different casings after the Drive resync).
          .replace(/\s+(z['"]?t['"]?l|zt['"]?zl|ztzl|ztl|zatzal|z['"]?l|shlita)\.?\s*$/i, '')
          // Collapse honorific apostrophes so "Ha'Rav" == "HaRav" in
          // the priority lookup. Applied AFTER trailing-strip so we
          // don't remove real apostrophes elsewhere in the string.
          .replace(/\bha['\u2019]?rav\b/i, 'harav')
          .replace(/\br['\u2019]\s+/i, 'r ')
          .trim();
      const PRIORITY_KEYS = [
        'harav henach leibowitz',
        "ha'rav henach leibowitz",
        'rabbi henach leibowitz',
        'harav dovid harris',
        'ha rav dovid harris',
        'rabbi dovid harris',
        'harav akiva grunblatt',
        'rabbi akiva grunblatt',
        'harav shaul opoczynski',
        'rabbi shaul opoczynski',
      ];
      const priorityIndex = (name) => {
        const key = normalizeSpeakerName(name);
        const idx = PRIORITY_KEYS.indexOf(key);
        return idx === -1 ? 999 : idx;
      };
      // Speakers that are actually collection folders / holding buckets —
      // NEVER shown on the home page even when they have hundreds of
      // recordings. They still appear on the More Speakers page.
      const HOMEPAGE_EXCLUDE = new Set([
        '-new needs to be sorted',
        'new needs to be sorted',
        'queens vaadim 2005-2025',
        'queens vaadim',
        'akiva paris',   // canonical display name from the alias layer
        "akiva paris's recordings from queens 2025-2026",
        "akiva paris's recordings from queens",
        "akiva paris's all star vaadim picks",
        'akiva paris from queens',
        'tiferes yisroel guest speakers',
        'random shiurim',
        'random shiurim (brooklyn)',
        'miami recordings',
        'old vaad otzar',
        'vaad otzar root',
        'mti guest speakers',
        'guest speakers',
        // Feb-2026 (user): Sisterhood back on the home grid, keep only
        // the "meetings of the sisterhood" bucket variant hidden.
        // 'sisterhood',
        // 'meetings of the sisterhood',
      ]);
      const isCollectionBucket = (name) =>
        HOMEPAGE_EXCLUDE.has((name || '').trim().toLowerCase());

      // Feb-2026: only proper honorifics show on the home grid. Anyone
      // without a HaRav / Rabbi / R' / Rav / Dr / Rebbetzin prefix drops
      // to the More Speakers page (they still exist elsewhere).
      // Regex: honorific prefixes followed by either a word boundary
      // (Rabbi, Rav, HaRav, Dr) OR — for the "R'" family — followed by
      // an apostrophe/period and then whitespace (the "\b" after an
      // apostrophe fails because both `'` and ` ` are non-word chars,
      // silently dropping every "R' Foo" speaker from the home grid).
      const HONORIFIC_RE = /^(ha['\u2019]?rav|hagaon|harav|rabbi|rebbetzin|rebbe|reb|dr[.]?|mrs|rav)\b/i;
      const RAV_SHORT_RE = /^r['\u2019.]\s+/i; // "R' Foo" or "R. Foo"
      const hasHonorific = (name) => {
        const n = (name || '').trim();
        return HONORIFIC_RE.test(n) || RAV_SHORT_RE.test(n);
      };

      const withImages = (speakersData || []).filter(
        (s) => {
          if (isCollectionBucket(s.name)) return false;
          // Feb-2026: only names that start with a proper honorific
          // (HaRav / Rabbi / R' / Rav / Dr / Rebbetzin / Mrs) appear on
          // the home page. Sisterhood is a special-cased bucket we DO
          // want featured, and the R' Yitzchak Schwartz placeholder
          // exception stays too.
          const nm = (s.name || '').trim();
          const norm = normalizeSpeakerName(nm).replace(/['\u2018\u2019]/g, "");
          const isSisterhood = /sisterhood/i.test(nm);
          const isSchwartz = norm === 'r yitzchak schwartz';
          // Feb-2026 (user): "Are You A Lamdan?", "Hashkafa" and
          // "Hespedim" are categorical speaker buckets with tons of
          // shiurim (53/434/87). Users want them on the home grid so
          // those pages actually get traffic. Their profile images
          // point at ?original=1 already so they render as clean
          // (non-translucent) portraits per Permanent Rule 20.
          const CATEGORY_ON_HOME = new Set(['are you a lamdan', 'hashkafa', 'hespedim']);
          const isCategoryOnHome = CATEGORY_ON_HOME.has(
            norm.replace(/\?$/, '').trim()
          );
          if (isCategoryOnHome) return true;
          if (!isSisterhood && !isSchwartz && !hasHonorific(nm)) return false;
          if (isSchwartz) return true;
          // Rule: any speaker with 70+ recordings is always on the home
          // page even if we don't have an icon uploaded yet. SpeakerCard
          // renders the same figure placeholder as the Schwartz case
          // when we flag `placeholderFigure` on the object below.
          if ((s.recordings || 0) >= 70) return true;
          // Accept absolute http URLs AND relative /api/... URLs (post
          // Feb-2026 migration: portraits are served as
          // /api/portrait/cutout/{drive_id} so we no longer require an
          // absolute host prefix here).
          return s.image && typeof s.image === 'string' && (s.image.startsWith('http') || s.image.startsWith('/'));
        }
      ).map((s) => {
        const hasImage = s.image && typeof s.image === 'string' && (s.image.startsWith('http') || s.image.startsWith('/'));
        // Mark iconless-but-featured speakers so SpeakerCard renders the
        // blue+gold empty figure instead of the initials avatar.
        return hasImage ? s : { ...s, placeholderFigure: true };
      });
      withImages.sort((a, b) => {
        const pa = priorityIndex(a.name);
        const pb = priorityIndex(b.name);
        if (pa !== pb) return pa - pb;
        return (b.recordings || 0) - (a.recordings || 0);
      });
      setSpeakers(withImages);
      // Persist to localStorage so a return visit renders instantly
      // before the network round-trip completes. The Service Worker
      // still refreshes in the background.
      try {
        localStorage.setItem('vaad_home_recordings_v1', JSON.stringify(recordingsData || []));
        localStorage.setItem('vaad_home_speakers_v1', JSON.stringify(withImages));
      } catch { /* quota */ }
      // Prime the studio-backdrop palette using the FINAL grid order
      // so `SpeakerCard` can assign colors round-robin. This maximises
      // the distance between any two identical tile colors on screen.
      primePaletteOrder(withImages.map((s) => s.name));

      // Load top topics (filter out the meta "General Shiur" bucket)
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/topics`)
        .then((r) => r.json())
        .then((all) => {
          const filtered = (all || []).filter((t) => t.name !== 'General Shiur');
          setTopics(filtered.slice(0, 10));
        })
        .catch(() => {});
      // Daily Featured (Feb-2026, user): must be about the upcoming
      // parsha or next holiday, with an intriguing title, from a speaker
      // that appears on the home grid. Compute AFTER speakers land so
      // we can filter by `speakerHomeNames`.
      // Lightning Pass 2 (Feb-2026): FIRE-AND-FORGET — never `await`
      // this before releasing the loading spinner. Doing so used to
      // hold the home page in a full-screen "loading" state for the
      // duration of this 200-400ms request even though speakers +
      // recordings were already in hand.
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/calendar/upcoming?limit_each=40`)
        .then((r) => r.json())
        .then((upc) => {
          if (!upc) return;
          const speakerSet = new Set((speakersData || []).map((s) => s.name));
          const goodTitle = (t) => {
            const s = String(t || '').trim();
            if (s.length < 4) return false;
            if (/^[\s#.\-_]*\d+[\s#.\-_]*$/.test(s)) return false;
            if (/^(shmuz|shiur|part|track|recording)\s*#?\s*\d+$/i.test(s)) return false;
            return /[a-zA-Z]{3,}/.test(s);
          };
          // Prefer holiday first (rarer, more immediate), then parsha.
          const pool = [...(upc.holiday_recommendations || []), ...(upc.parsha_recommendations || [])];
          const pick = pool.find((r) => speakerSet.has(r.speaker) && goodTitle(r.title))
                    || pool.find((r) => speakerSet.has(r.speaker));
          if (pick) setUpcomingFeatured(pick);
        })
        .catch(() => { /* fall through to generic featuredRecording pick */ });
    } catch (error) {
      console.error('Error loading data:', error);
      // Set empty arrays even on error to allow page to render
      setRecordings([]);
      setSpeakers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Feb-2026 (user "a big problem is it says loading recordings for a
  // while"): the old full-screen "Loading recordings..." spinner blocked
  // the ENTIRE home page behind the /api/speakers round-trip on every
  // cold launch — right after the user already sat through the boot
  // splash. We now render the real page shell instantly and drop
  // skeleton tiles into the Speakers grid until data lands, so the app
  // appears immediately instead of showing a loading wall.
  const showSpeakerSkeletons = loading && speakers.length === 0;

  const featuredRecordings = recordings.slice(0, 5);
  const recommendedRecordings = recordings.slice(5, 9);
  // Daily Featured (Feb-2026, user):
  //   • MUST come from an upcoming/this-shabbos topic (parsha or holiday)
  //   • speaker MUST appear on the home grid (i.e. has a profile pic)
  //   • title MUST be "intriguing" (not a raw timestamp/number)
  // Falls back gracefully so the section still renders on days where the
  // topic pull is empty.
  const speakerHomeNames = new Set((speakers || []).map((s) => s.name));
  const isGoodTitle = (t) => {
    const s = String(t || '').trim();
    if (s.length < 4) return false;
    if (/^[\s#.\-_]*\d+[\s#.\-_]*$/.test(s)) return false;
    if (/^(shmuz|shiur|part|track|recording)\s*#?\s*\d+$/i.test(s)) return false;
    return /[a-zA-Z]{3,}/.test(s);
  };
  const featuredRecording =
    upcomingFeatured ||
    recordings.find((r) => speakerHomeNames.has(r.speaker) && isGoodTitle(r.title)) ||
    recordings.find((r) => speakerHomeNames.has(r.speaker)) ||
    recordings[0] ||
    {};

  return (
    <div className="flex h-screen bg-[#ffffff] overflow-hidden">
      <Sidebar activeItem="Home" />
      
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto p-4 pt-3 sm:p-8 sm:pt-8">
          {/* Hero Section with Logo — compact so speakers row shows above the fold */}
          <section className="mb-4 flex items-center gap-4">
            <img
              src="/vaad-otzar-logo.jpg"
              alt="The Vaad Otzar"
              className="w-16 h-16 md:w-20 md:h-20 rounded-xl shadow-lg object-cover border-2 border-[#9a7b24] shrink-0"
            />
            <div className="min-w-0">
              <h1 className="text-[2.6rem] sm:text-5xl md:text-[3.4rem] font-bold text-[#0f1729] dark:text-white leading-none tracking-tight" data-testid="home-title">
                The Vaad Otzar
              </h1>
              <p data-testid="home-subheader" className="text-sm text-[#4b5563] dark:text-[#8591ad]">
                A library of shiurim compiled by the Talmidim of Yeshivas Chofetz Chaim
              </p>
            </div>
            <div className="ml-auto shrink-0 self-start mt-6 sm:mt-0 sm:self-center" data-testid="home-access-slot">
              <AccessPill compact />
            </div>
          </section>

          {/* Prominent global search — moved from the mobile bottom nav
              (Feb 2026, user). Freeing up a bottom-nav slot for
              Transcriptions and giving search a permanent home
              directly above the Continue Listening rail. */}
          <button
            type="button"
            onClick={openSearch}
            className="w-full mb-6 flex items-center gap-3 pl-4 pr-3 py-3 rounded-2xl text-left liquid-glass hover:shadow-lg transition-all duration-300 group"
            data-testid="home-search-bar"
            style={{ background: 'rgba(255,255,255,0.85)', borderColor: 'rgba(201,164,55,0.35)' }}
            aria-label="Open global search"
          >
            <span className="w-9 h-9 rounded-xl bg-[#f6e2a4]/60 flex items-center justify-center shrink-0 group-hover:bg-[#f6e2a4] transition-colors">
              <Search className="w-4 h-4 text-[#9a7b24]" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-semibold text-[#0f1729] leading-tight">
                Search shiurim, speakers, topics…
              </span>
              <span className="block text-[11px] text-[#8a8f99] leading-tight">
                Tap to open the full search
              </span>
            </span>
          </button>

          <ContinueListening />

          {/* This Shabbos + Next Holiday hero */}
          <UpcomingHero compact />

          {/* Speakers Section */}
          <section className="mb-12">
            <h2 className="text-[#0f1729] text-2xl font-semibold mb-6">Speakers</h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-3 sm:gap-4 md:gap-6" data-testid="home-speakers-grid">
              {showSpeakerSkeletons && Array.from({ length: 14 }).map((_, i) => (
                <div key={`spk-skel-${i}`} className="animate-pulse" data-testid="speaker-skeleton">
                  <div className="aspect-square rounded-2xl bg-[#ece8e0]" />
                  <div className="h-3 mt-3 mx-auto w-3/4 rounded bg-[#ece8e0]" />
                </div>
              ))}
              {speakers.map((speaker, index) => (
                <SpeakerCard 
                  key={speaker.name} 
                  speaker={{
                    id: speaker.name,
                    name: speaker.name,
                    recordings: speaker.recordings,
                    guest_recordings: speaker.guest_recordings,
                    // Feb-2026: accept relative /api/portrait/white/... URLs
                    // from the whitened-portrait pipeline in addition to
                    // absolute http(s) sources.
                    image: speaker.image && (speaker.image.startsWith('http') || speaker.image.startsWith('/')) ? speaker.image : null
                  }}
                  onClick={() => navigate(`/speaker/${encodeURIComponent(speaker.name)}`)}
                  onRenamed={loadData}
                  // Feb-2026 (user "never see them pop up and load"):
                  // ALL homepage portraits load eagerly now. The old
                  // 14-tile cutoff meant scrolling users saw a visible
                  // fade-in on tiles 15+ as their IntersectionObserver
                  // fired. Portraits are small (15-40 KB WebPs) and
                  // Brotli+HTTP/2 handle the concurrent load fine —
                  // trade a bit of cold-load bandwidth for zero pop-in.
                  priority="eager"
                /> 
              ))}
            </div>
          </section>

          {topics.length > 0 && (
            <section className="mb-12" data-testid="home-top-topics">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[#9a7b24] font-bold mb-1">Explore by</p>
                  <h2 className="text-[#0f1729] dark:text-white text-2xl font-semibold">Top Topics</h2>
                </div>
                <Button
                  variant="ghost"
                  className="hidden sm:inline-flex text-[#9a7b24] hover:text-[#0f1729] hover:bg-[#efede7] dark:hover:bg-white/5 dark:hover:text-white"
                  onClick={() => navigate('/topics')}
                  data-testid="home-topics-viewall"
                >
                  View all
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {topics.map((t) => (
                  <button
                    key={t.slug}
                    onClick={() => navigate(`/topic/${t.slug}`)}
                    className="text-left rounded-xl bg-white border border-[#e1ddd6] px-4 py-3 hover:border-[#9a7b24] hover:-translate-y-0.5 hover:shadow-md transition-all duration-300 dark:bg-[hsl(222,40%,14%)] dark:border-white/10 dark:hover:border-[#9a7b24]"
                    data-testid={`home-topic-${t.slug}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-serif text-[16px] font-semibold text-[#0f1729] dark:text-white truncate">
                          {t.name}
                        </div>
                        <div className="text-xs text-[#8a8f99] dark:text-[#8591ad] mt-0.5">
                          {t.count.toLocaleString()} Shiurim
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-[#9a7b24] shrink-0" />
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}

          <HomeFeaturedSeries />

          <FeaturedSection recording={featuredRecording} navigate={navigate} speakers={speakers} />

          {SHOW_EMAIL_SUB && <DailyShiurSignupCard />}

          {/* Random Shiur button — the "surprise me" bottom-of-page CTA
              Feb 2026 (user): only picks from speakers with a profile pic AND
              a title that isn't just numbers/nonsense. Adds a spin-wheel
              animation on the icon while loading. */}
          <RandomShiurButton navigate={navigate} speakers={speakers} />

          {/* Feb-2026 (#34): Quote of the day — deterministic per-day
              gem pulled from a random cached transcript. Sits at the
              very bottom of the homescreen. */}
          <QuoteOfTheDayBanner />
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default HomePage;

/** Random Shiur button with a spin-wheel animation. Only pulls from
 *  speakers who have a profile picture AND whose title isn't a plain
 *  number ("Shmuz #143") — the user wants intriguing / meaningful
 *  titles only. Falls back to the plain /random endpoint if we can't
 *  find one within a small number of retries. */
const RandomShiurButton = ({ navigate, speakers }) => {
  const [spinning, setSpinning] = React.useState(false);

  const isIntriguing = (title) => {
    const t = String(title || '').trim();
    if (t.length < 4) return false;
    // Reject pure numbers / "Shmuz 45" / "#123 " / audio-file placeholders.
    if (/^[\s#.\-_]*\d+[\s#.\-_]*$/.test(t)) return false;
    if (/^(shmuz|shiur|part|track|recording)\s*#?\s*\d+$/i.test(t)) return false;
    if (/^audio\s+recording$/i.test(t)) return false;
    // Must contain at least one letter cluster
    if (!/[a-zA-Z]{3,}/.test(t)) return false;
    return true;
  };

  const speakersWithPics = React.useMemo(() => {
    const s = new Set();
    (speakers || []).forEach((sp) => {
      if (sp && sp.image && typeof sp.image === 'string' && (sp.image.startsWith('http') || sp.image.startsWith('/'))) {
        s.add(sp.name);
      }
    });
    return s;
  }, [speakers]);

  const spin = async () => {
    if (spinning) return;
    setSpinning(true);
    try {
      let chosen = null;
      // Try up to 12 times to find a recording matching the constraints.
      // Feb-2026 (user): must also reject audio/x-ms-wma — Chrome can't
      // demux WMA-in-.mp3 so the player pops "Audio streaming unavailable"
      // banners. Those files still surface via speaker pages / search but
      // must never be the answer to "surprise me".
      for (let attempt = 0; attempt < 12; attempt += 1) {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recordings/random`);
        if (!res.ok) continue;
        const data = await res.json();
        if (!data || !data.drive_file_id) continue;
        if (data.mime_type === 'audio/x-ms-wma') continue;
        const hasPic = speakersWithPics.has(data.speaker);
        const goodTitle = isIntriguing(data.title);
        if (hasPic && goodTitle) { chosen = data; break; }
        if (!chosen) chosen = data; // remember something in case retries fail
      }
      // Give the spin animation at least 900ms so it feels like a wheel
      await new Promise((r) => setTimeout(r, 900));
      if (chosen?.drive_file_id) navigate(`/player/${chosen.drive_file_id}`);
    } catch (err) {
      console.error('random shiur error', err);
    } finally {
      setSpinning(false);
    }
  };

  return (
    <section className="my-10" data-testid="home-random-shiur-section">
      <button
        type="button"
        onClick={spin}
        disabled={spinning}
        className="group w-full rounded-2xl bg-gradient-to-br from-[#0f1729] via-[#1f2b47] to-[#0f1729] hover:from-[#1f2b47] hover:via-[#28345c] hover:to-[#1f2b47] transition-all p-6 md:p-8 border border-[#e5c976]/30 shadow-lg hover:shadow-xl overflow-hidden relative disabled:cursor-wait"
        data-testid="home-random-shiur-btn"
      >
        <div className="absolute inset-0 opacity-[0.06] bg-[radial-gradient(circle_at_30%_20%,#e5c976_0%,transparent_60%)] pointer-events-none" />
        <div className="relative flex items-center gap-4 md:gap-6">
          <div
            className={`w-14 h-14 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-[#c9a437] to-[#8c691b] flex items-center justify-center text-white shadow-lg shrink-0 transition-transform ${
              spinning ? 'animate-spin-slow' : 'group-hover:rotate-[8deg]'
            }`}
            style={spinning ? { animation: 'randomShiurSpin 0.6s linear infinite' } : undefined}
          >
            <Shuffle className="w-6 h-6 md:w-7 md:h-7" />
          </div>
          <div className="min-w-0 flex-1 text-left">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#e5c976] font-bold mb-1">
              {spinning ? 'Spinning the wheel…' : 'Surprise me'}
            </p>
            <h3 className="text-white text-xl md:text-2xl font-semibold leading-tight">
              {spinning ? 'Picking a shiur for you' : 'Play a Random Shiur'}
            </h3>
            <p className="text-white/60 text-sm mt-1">
              Pick a shiur at random from the library — perfect for daily learning without decision fatigue.
            </p>
          </div>
          <ChevronRight className="w-6 h-6 text-[#e5c976] shrink-0 group-hover:translate-x-1 transition-transform" />
        </div>
        <style>{`
          @keyframes randomShiurSpin {
            from { transform: rotate(0deg); }
            to   { transform: rotate(360deg); }
          }
        `}</style>
      </button>
    </section>
  );
};


/** Feb-2026 (#34) — Quote of the Day.
 *  Bottom-of-homescreen banner. Pulls a deterministic random quote
 *  from /api/transcripts/random-quote (the same for every visitor on
 *  a given day) and shows it as an inviting serif blockquote linking
 *  into the shiur at the exact word. Silently hides if the API is
 *  unavailable so the homepage never breaks. */
const QuoteOfTheDayBanner = () => {
  const [quote, setQuote] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    let cancelled = false;
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!backend) return undefined;
    // Feb-2026 superfast pass: defer to idle so the quote banner never
    // competes with LCP or above-the-fold API calls on cold loads.
    // Falls back to a short setTimeout for browsers without RIC (iOS).
    const kick = () => {
      if (cancelled) return;
      fetch(`${backend}/api/transcripts/random-quote`)
        .then((r) => r.ok ? r.json() : null)
        .then((j) => { if (!cancelled && j && j.text) setQuote(j); })
        .catch(() => { /* silent */ });
    };
    let handle;
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      handle = window.requestIdleCallback(kick, { timeout: 2500 });
    } else {
      handle = setTimeout(kick, 1200);
    }
    return () => {
      cancelled = true;
      if (typeof window !== 'undefined' && 'cancelIdleCallback' in window && handle) {
        try { window.cancelIdleCallback(handle); } catch { /* noop */ }
      } else if (handle) {
        clearTimeout(handle);
      }
    };
  }, []);
  if (!quote || !quote.text) return null;
  const jumpToQuote = () => {
    if (!quote.drive_file_id) return;
    const params = [];
    if (Number.isFinite(quote.word_idx)) params.push(`word=${quote.word_idx}`);
    if (Number.isFinite(quote.start)) params.push(`t=${Math.floor(quote.start)}`);
    navigate(`/player/${quote.drive_file_id}${params.length ? `?${params.join('&')}` : ''}`);
  };
  return (
    <section
      className="my-10"
      data-testid="home-quote-of-day-section"
    >
      <button
        type="button"
        onClick={jumpToQuote}
        data-testid="home-quote-of-day"
        className="group w-full text-left rounded-2xl bg-gradient-to-br from-[#faf3d4] via-[#fdf7dc] to-[#f6e2a4]/40 hover:from-[#fdf7dc] hover:via-white hover:to-[#faf3d4] transition-colors p-6 md:p-8 border border-[#c9a437]/40 hover:border-[#c9a437] shadow-md hover:shadow-lg relative overflow-hidden"
      >
        <div className="absolute -top-6 -left-4 opacity-10 text-[160px] leading-none font-serif text-[#c9a437] pointer-events-none select-none">
          “
        </div>
        <div className="relative flex items-start gap-4">
          <div className="w-11 h-11 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-[#c9a437] to-[#8c691b] flex items-center justify-center text-white shadow-md shrink-0">
            <Quote className="w-5 h-5 md:w-5 md:h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#9a7b24] font-bold mb-1">
              Quote of the Day
            </p>
            <blockquote className="text-[#3a2810] text-lg md:text-xl leading-relaxed font-serif italic mb-2">
              {quote.text}
            </blockquote>
            <div className="text-xs md:text-sm text-[#8a7a3a]">
              {quote.speaker && <span className="font-semibold text-[#5a4610]">{quote.speaker}</span>}
              {quote.shiur_title && (
                <>
                  <span className="mx-1.5 opacity-40">·</span>
                  <span className="truncate max-w-full inline-block align-bottom">{quote.shiur_title}</span>
                </>
              )}
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-[#9a7b24] shrink-0 group-hover:translate-x-1 transition-transform" />
        </div>
      </button>
    </section>
  );
};
